import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ChevronLeft, Cog, Truck, Wrench, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { partners as mockPartners } from '../data/mock';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { language } = useLanguage();
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroLoading, setHeroLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [partners, setPartners] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [pageImages, setPageImages] = useState({});

  // Helper to get text in current language
  const t = (textObj) => getText(language, textObj.en, textObj.fr, textObj.ar);
  const home = staticTexts.home;
  const stats = staticTexts.stats;
  const features = staticTexts.features;

  // Fetch data from API
  useEffect(() => {
    // Fetch Hero Slides
    fetch(`${BACKEND_URL}/api/hero-slides`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setHeroSlides(data);
        }
      })
      .catch(err => console.error('Error fetching hero slides:', err))
      .finally(() => setHeroLoading(false));

    // Fetch Partners
    fetch(`${BACKEND_URL}/api/partners`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setPartners(data);
        } else {
          setPartners(mockPartners);
        }
      })
      .catch(() => setPartners(mockPartners));

    // Fetch Site Content
    fetch(`${BACKEND_URL}/api/content`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          const contentMap = {};
          data.forEach(item => {
            contentMap[item.key] = {
              en: item.value_en,
              fr: item.value_fr
            };
          });
          setSiteContent(contentMap);
        }
      })
      .catch(err => console.error('Error fetching content:', err));

    // Fetch Page Images
    fetch(`${BACKEND_URL}/api/page-images/home`)
      .then(res => res.json())
      .then(data => setPageImages(data || {}))
      .catch(err => console.error('Error fetching page images:', err));
  }, []);

  // Default hero images for slideshow
  const defaultHeroImages = [
    {
      id: 'default-1',
      image_url: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1400&q=80',
      title_fr: 'Équipements Agricoles',
      title_en: 'Agricultural Equipment',
      title_ar: 'المعدات الزراعية'
    },
    {
      id: 'default-2', 
      image_url: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=1400&q=80',
      title_fr: 'Pièces de Rechange',
      title_en: 'Spare Parts',
      title_ar: 'قطع الغيار'
    },
    {
      id: 'default-3',
      image_url: 'https://images.unsplash.com/photo-1530267981375-f0de937f5f13?w=1400&q=80',
      title_fr: 'Machines Industrielles',
      title_en: 'Industrial Machinery',
      title_ar: 'الآلات الصناعية'
    }
  ];

  // Check if image URL is valid (not pointing to old/broken domain)
  const isValidImageUrl = (url) => {
    if (!url) return false;
    const currentDomain = window.location.hostname;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname === currentDomain || 
             urlObj.hostname.includes('unsplash') || 
             urlObj.hostname.includes('pexels') ||
             !urlObj.hostname.includes('preview.emergentagent.com') ||
             urlObj.hostname === 'cms-admin-panel-1.preview.emergentagent.com';
    } catch {
      return false;
    }
  };
  
  // Filter valid slides or use defaults
  const validSlides = heroSlides.filter(slide => isValidImageUrl(slide.image_url));
  const displaySlides = validSlides.length > 0 ? validSlides : defaultHeroImages;
  const isRTL = language === 'ar';

  // Auto-advance slideshow
  useEffect(() => {
    if (displaySlides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % displaySlides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [displaySlides.length]);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % displaySlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + displaySlides.length) % displaySlides.length);
  };

  const getContent = (key, defaultEn, defaultFr, defaultAr = null) => {
    if (siteContent[key]) {
      if (language === 'ar') return siteContent[key].ar || siteContent[key].fr;
      if (language === 'fr') return siteContent[key].fr;
      return siteContent[key].en;
    }
    if (language === 'ar') return defaultAr || defaultFr;
    if (language === 'fr') return defaultFr;
    return defaultEn;
  };

  const statsData = [
    { value: '25+', label: t(stats.years) },
    { value: '10+', label: t(stats.partners) },
    { value: '500+', label: t(stats.clients) },
  ];

  const featuresData = [
    {
      icon: Cog,
      title: t(features.quality.title),
      desc: t(features.quality.desc)
    },
    {
      icon: Truck,
      title: t(features.delivery.title),
      desc: t(features.delivery.desc)
    },
    {
      icon: Wrench,
      title: t(features.service.title),
      desc: t(features.service.desc)
    },
    {
      icon: Users,
      title: t(features.support.title),
      desc: t(features.support.desc)
    },
  ];

  return (
    <Layout>
      {/* Hero Section with Slideshow */}
      <section className="relative min-h-[90vh] flex items-center bg-slate-100 overflow-hidden">
        {/* Background slideshow */}
        <div className="absolute inset-0">
          {!heroLoading ? (
            <>
              {displaySlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide % displaySlides.length ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={slide.image_url}
                    alt={slide.title_fr || `Slide ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient changes direction for RTL */}
                  <div className={`absolute inset-0 ${
                    isRTL 
                      ? 'bg-gradient-to-l from-slate-100 via-slate-100/90 to-slate-100/30' 
                      : 'bg-gradient-to-r from-slate-100 via-slate-100/90 to-slate-100/30'
                  }`} />
                </div>
              ))}
              
              {/* Slide controls */}
              {displaySlides.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-all"
                  >
                    <ChevronLeft className="w-6 h-6 text-slate-700" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-all"
                  >
                    <ChevronRight className="w-6 h-6 text-slate-700" />
                  </button>
                  
                  {/* Slide indicators */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                    {displaySlides.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentSlide % displaySlides.length ? 'bg-slate-800 w-8' : 'bg-slate-400'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            // Loading state - just show gradient background
            <div className="absolute inset-0 bg-gradient-to-r from-slate-100 to-slate-200" />
          )}
        </div>
        
        {/* Hero content - positioned based on language */}
        <div className={`container mx-auto px-6 relative z-10 ${isRTL ? 'text-right' : 'text-left'}`}>
          <div className={`max-w-2xl ${isRTL ? 'mr-0 ml-auto' : ''}`}>
            <p className="text-sm uppercase tracking-widest text-slate-400 mb-4">
              {t(home.since)}
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-slate-800 mb-6 leading-[1.1]">
              {t(home.heroTitle1)}<br />
              <span className="font-semibold text-slate-900">
                {t(home.heroTitle2)}
              </span>
            </h1>
            <p className="text-lg text-slate-500 mb-10 leading-relaxed max-w-lg">
              {t(home.heroSubtitle)}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/equipment">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-sm rounded-full">
                  {t(home.viewEquipment)}
                  <ArrowRight className={`${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'}`} size={18} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-200 px-8 py-6 text-sm rounded-full">
                  {t(home.getInTouch)}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-800 py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-3 gap-8">
            {statsData.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-light text-white mb-1">{stat.value}</p>
                <p className="text-xs uppercase tracking-widest text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                {t(home.aboutUs)}
              </p>
              <h2 className="text-4xl md:text-5xl font-light text-slate-800 mb-6">
                {t(home.yourTrusted)}<br />
                <span className="font-semibold text-slate-900">{t(home.partner)}</span>
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                {t(home.aboutDescription)}
              </p>
              <Link to="/services" className="inline-flex items-center text-sm font-medium text-slate-800 hover:text-slate-600 transition-colors">
                {t(home.discoverServices)}
                <ChevronRight className={`${language === 'ar' ? 'mr-1 rotate-180' : 'ml-1'}`} size={16} />
              </Link>
            </div>
            <div className="relative">
              <img
                src={pageImages.about?.image_url || "https://images.unsplash.com/photo-1530267981375-f0de937f5f13?w=800&q=80"}
                alt="Modern Agriculture"
                className="rounded-2xl w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-slate-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
              {t(home.whyChooseUs)}
            </p>
            <h2 className="text-4xl font-light text-slate-800">
              {t(home.excellence)} <span className="font-semibold">{t(home.everyDetail)}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuresData.map((feature, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300">
                <feature.icon className="text-slate-400 mb-6" size={32} strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-slate-800 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
              {t(home.ourPartners)}
            </p>
            <h2 className="text-4xl font-light text-slate-800">
              {t(home.trustedBy)} <span className="font-semibold">{t(home.industry)}</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {partners.slice(0, 8).map((partner) => (
              <Link
                key={partner.id}
                to={`/partner/${partner.id}`}
                className="group bg-white rounded-xl p-8 flex items-center justify-center hover:shadow-md transition-all duration-300"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="max-h-12 w-auto grayscale group-hover:grayscale-0 transition-all duration-300"
                />
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/partners">
              <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-200 px-8 py-6 text-sm rounded-full">
                {t(home.viewAllPartners)}
                <ChevronRight className={`${language === 'ar' ? 'mr-1 rotate-180' : 'ml-1'}`} size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
              {t(home.readyToWork)}
            </h2>
            <p className="text-slate-400 mb-10">
              {t(home.contactTeam)}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-sm rounded-full">
                  {t(home.contactUs)}
                </Button>
              </Link>
              <Link to="/become-partner">
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-700 px-8 py-6 text-sm rounded-full">
                  {language === 'en' ? 'Become a Partner' : language === 'ar' ? 'كن شريكاً' : 'Devenir Partenaire'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
